import { useMutation, useQueryClient } from "react-query";
import axios, { AxiosError } from "axios";
import { FormikHelpers } from "formik";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "i18n";
import { IGraduatesApplication } from "../types";
import { config } from "../../../../config/common";
import { checkForRecurlyPostCodeError } from "../../../../utils/checkForRecurlyPostCodeError";
import {
  GET_GRADUATES_APPLICATION_DATA_QUERY,
  gradsFormNavigationMap,
  GraduatesEndpoints,
} from "../constants";
import useSetGradsApplicationData from "./useSetGradsApplicationData";

function useSubmitGradsApplicationForm<T>(
  onlineApplicationId: string | number | undefined,
  securityKey: string | undefined,
  errorCallback: () => void,
  endpoint: GraduatesEndpoints
) {
  const queryClient = useQueryClient();
  const setGradsApplicationData = useSetGradsApplicationData();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { mutateAsync, error } = useMutation<
    { data: IGraduatesApplication },
    AxiosError,
    T
  >({
    mutationFn: (data) => {
      return axios.post(
        `${config.userAccountApiUrl}/application/graduate/${endpoint}`,
        data,
        { params: { onlineApplicationId, securityKey } }
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries(GET_GRADUATES_APPLICATION_DATA_QUERY),
  });

  const submitForm = async (values: T, formikHelpers?: FormikHelpers<T>) => {
    try {
      const result = await mutateAsync(values);

      setGradsApplicationData(result.data);
      navigate(gradsFormNavigationMap[endpoint]);
    } catch (error) {
      const axiosError = error as AxiosError;

      if (formikHelpers) {
        const hasPostCodeValidation = checkForRecurlyPostCodeError(
          { cause: axiosError?.response?.data },
          formikHelpers?.setFieldError,
          t
        );

        if (!hasPostCodeValidation) {
          errorCallback();
        }
      }
    }
  };

  return {
    submitForm,
    error,
  };
}

export default useSubmitGradsApplicationForm;
